import React from 'react';
import { motion } from "framer-motion";
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';

const VendorTerms = () => {
  return (
    <>
      <Header />
      <div className="min-h-screen bg-background text-foreground overflow-x-hidden relative">
        {/* Animated Background Blobs */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
          <motion.div
            animate={{ x: [0, 50, 0], y: [0, 40, 0] }}
            transition={{ duration: 24, repeat: Infinity, ease: "linear" }}
            className="absolute top-[10%] -left-[5%] w-[40%] h-[40%] bg-primary/5 rounded-full blur-[100px]"
          />
          <motion.div
            animate={{ x: [0, -40, 0], y: [0, 80, 0] }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            className="absolute bottom-[10%] -right-[5%] w-[35%] h-[35%] bg-accent/5 rounded-full blur-[100px]"
          />
        </div>

        <section id="vendor-terms" className="relative z-10 pt-32 pb-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-4xl mx-auto"
            >
              {/* Header */}
              <div className="text-center mb-16 space-y-4">
                <h1 className="font-display text-4xl md:text-6xl font-bold tracking-tight">
                  Vendor <span className="text-primary">Terms</span>
                </h1>
                <p className="text-muted-foreground font-medium uppercase tracking-widest text-xs">Partnership • Agreement • Usage</p>
                <p className="text-muted-foreground mt-2">Effective Date: January 30, 2026</p>
              </div>

            {/* Content */}
            <div className="prose prose-lg max-w-none">
              <div className="bg-muted/50 rounded-2xl p-8 md:p-12 space-y-8 text-foreground border border-border">
                
                {/* Introduction */}
                <div className="p-4 bg-background border border-border rounded-lg mb-8">
                  <p className="text-muted-foreground"><strong>Platform:</strong> Quro (Website & Mobile Application)</p>
                  <p className="text-muted-foreground"><strong>Operator:</strong> Quick Retail Online Private Limited (“Quro”, “We”, “Us”, “Our”)</p>
                  <p className="text-muted-foreground text-sm mt-1"><strong>FSSAI Reg. No.:</strong> 30260424124243466</p>
                  <p className="text-muted-foreground mt-2 text-sm">This Vendor Agreement ("Agreement") governs the relationship between Quro and the business entity ("Vendor", "Partner", "You") listing products/services on our platform.</p>
                </div>

                {/* 1. PURPOSE OF AGREEMENT */}
                <div>
                  <h2 className="text-2xl font-bold mb-4 font-display">1. PURPOSE OF AGREEMENT</h2>
                  <p className="leading-relaxed mb-4 text-muted-foreground">
                    This Agreement sets out the terms under which Quro will provide a digital platform for the Vendor to list, market, and sell their products (including restaurant menus and retail catalogues) to Customers.
                  </p>
                </div>

                {/* 2. LICENSING & CONTENT USAGE */}
                <div>
                  <h2 className="text-2xl font-bold mb-4 font-display">2. LICENSING & CONTENT USAGE</h2>
                  <p className="leading-relaxed mb-4 text-muted-foreground">By registering as a Vendor on Quro, you grant us a non-exclusive, worldwide, royalty-free license to:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4 text-muted-foreground">
                    <li><strong>Display Menus & Catalogues:</strong> Use, host, and display your restaurant menus, product catalogues, descriptions, and pricing on the Platform.</li>
                    <li><strong>Brand Assets:</strong> Use your business name, logos, and trademarks for the purpose of identifying your store and marketing your products.</li>
                    <li><strong>Product Imagery:</strong> Use, resize, and optimize images of food, products, or storefronts provided by you or captured by Quro for promotional purposes.</li>
                    <li><strong>Sub-licensing:</strong> Grant sub-licenses to our delivery partners and marketing affiliates purely for operational and promotional needs.</li>
                  </ul>
                  <p className="leading-relaxed mt-4 text-muted-foreground">
                    You represent and warrant that you own or have the necessary rights to all content (images, descriptions, brand names) provided to Quro.
                  </p>
                </div>

                {/* 3. VENDOR OBLIGATIONS */}
                <div>
                  <h2 className="text-2xl font-bold mb-4 font-display">3. VENDOR OBLIGATIONS</h2>
                  <h3 className="text-xl font-semibold mb-2 text-foreground">3.1 Accuracy of Information</h3>
                  <p className="leading-relaxed mb-4 text-muted-foreground">The Vendor is responsible for:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4 text-muted-foreground">
                    <li>Maintaining real-time accuracy of stock levels and availability.</li>
                    <li>Ensuring that menu prices on Quro match (or are appropriately adjusted from) in-store prices.</li>
                    <li>Updating descriptions, allergen information, and dietary tags (e.g., Veg/Non-Veg).</li>
                  </ul>

                  <h3 className="text-xl font-semibold mt-6 mb-2 text-foreground">3.2 Order Fulfillment</h3>
                  <ul className="list-disc list-inside space-y-2 ml-4 text-muted-foreground">
                    <li>Accepting and processing orders within the agreed timelines.</li>
                    <li>Ensuring food/products are prepared, packaged, and handed over to delivery partners in optimal condition.</li>
                    <li>Adhering to FSSAI standards and other local food safety regulations.</li>
                  </ul>
                </div>

                {/* 4. INTELLECTUAL PROPERTY */}
                <div>
                  <h2 className="text-2xl font-bold mb-4 font-display">4. INTELLECTUAL PROPERTY</h2>
                  <p className="leading-relaxed mb-4 text-muted-foreground">
                    <strong>Vendor IP:</strong> All intellectual property rights in the Vendor's menu, catalogue, and brand assets remain the property of the Vendor.
                  </p>
                  <p className="leading-relaxed mb-4 text-muted-foreground">
                    <strong>Platform IP:</strong> All rights in the Quro Platform, including software, algorithms, data analytics, and user interface, belong exclusively to Quick Retail Online Private Limited.
                  </p>
                </div>

                {/* 5. PRICING & COMMISSIONS */}
                <div>
                  <h2 className="text-2xl font-bold mb-4 font-display">5. PRICING & COMMISSIONS</h2>
                  <p className="leading-relaxed mb-4 text-muted-foreground">
                    Quro shall charge a service fee/commission on every successful order as per the commercial terms agreed during onboarding. Quro reserves the right to adjust commission rates with a 30-day prior notice.
                  </p>
                </div>

                {/* 6. PAYMENTS & SETTLEMENTS */}
                <div>
                  <h2 className="text-2xl font-bold mb-4 font-display">6. PAYMENTS & SETTLEMENTS</h2>
                  <p className="leading-relaxed mb-4 text-muted-foreground">
                    Payments for successful orders (net of commissions and taxes) will be settled into the Vendor's registered bank account according to the agreed settlement cycle (e.g., T+2 or Weekly).
                  </p>
                </div>

                {/* 7. HYGIENE & QUALITY COMPLIANCE */}
                <div>
                  <h2 className="text-2xl font-bold mb-4 font-display">7. HYGIENE & QUALITY COMPLIANCE</h2>
                  <p className="leading-relaxed mb-4 text-muted-foreground">
                    Vendors must maintain high standards of hygiene and quality. Quro reserves the right to conduct periodic audits or request certifications. Negative customer feedback regarding quality may lead to temporary suspension of the storefront.
                  </p>
                </div>

                {/* 8. TERMINATION */}
                <div>
                  <h2 className="text-2xl font-bold mb-4 font-display">8. TERMINATION</h2>
                  <p className="leading-relaxed mb-4 text-muted-foreground">
                    Either party may terminate this agreement with a 15-day written notice. However, Quro may suspend or terminate Vendor access immediately in case of:
                  </p>
                  <ul className="list-disc list-inside space-y-2 ml-4 text-muted-foreground">
                    <li>Violation of safety or hygiene standards.</li>
                    <li>Fraudulent activities or misrepresentation.</li>
                    <li>Repeated failure to fulfill orders.</li>
                    <li>Inappropriate behavior towards delivery partners or customers.</li>
                  </ul>
                </div>

                {/* 9. LIABILITY & INDEMNITY */}
                <div>
                  <h2 className="text-2xl font-bold mb-4 font-display">9. LIABILITY & INDEMNITY</h2>
                  <p className="leading-relaxed text-muted-foreground">
                    The Vendor shall indemnify Quro against any claims, losses, or damages arising from food-borne illnesses, defective products, or infringement of third-party intellectual property by the Vendor's content.
                  </p>
                </div>

                {/* 10. GOVERNING LAW */}
                <div>
                  <h2 className="text-2xl font-bold mb-4 font-display">10. GOVERNING LAW</h2>
                  <p className="leading-relaxed text-muted-foreground">
                    This Agreement is governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the courts in Hyderabad, Telangana.
                  </p>
                </div>

                {/* 11. CONTACT */}
                <div>
                  <h2 className="text-2xl font-bold mb-4 font-display">11. CONTACT</h2>
                  <div className="mt-4 p-4 bg-background border border-border rounded-lg">
                    <p className="font-semibold text-foreground">Quro Vendor Support</p>
                    <p className="text-muted-foreground">Email: <a href="mailto:q@quro.today" className="text-primary hover:text-primary/80 transition-colors">q@quro.today</a></p>
                    <div className="text-muted-foreground mt-4 space-y-1">
                      <p className="font-semibold text-foreground">Registered Office:</p>
                      <p>Quick Retail Online Private Limited</p>
                      <p>24-21, Ashoknagar</p>
                      <p>Ramachandra Puram, Hyderabad-502032</p>
                    </div>
                    <div className="text-muted-foreground mt-4 space-y-1">
                      <p className="font-semibold text-foreground">HQ Office:</p>
                      <p>Suite 406, 4th floor, MyScape Weave</p>
                      <p>Financial District, Nankramaguda</p>
                      <p>Hyderabad-500032</p>
                    </div>
                  </div>
                </div>

              </div>
            </div>

            {/* Back to Top Button */}
            <div className="mt-12 text-center">
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="inline-flex items-center space-x-2 text-primary hover:text-primary/80 font-semibold transition-colors duration-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                </svg>
                <span>Back to Top</span>
              </button>
            </div>
            </motion.div>
          </div>
        </section>
        <Footer />
      </div>
    </>
  );
};

export default VendorTerms;
